import { Box, Divider } from '@mui/material';
import { RECENTLY_LAUNCHED_FILTERS } from 'components/constants/Constants';
import { getFilterOptions } from 'helpers/Search';
import { useMemo } from 'react';
import { ListGroup } from 'react-bootstrap';
import { Trans } from 'react-i18next';
import { tss } from 'tss-react/mui';
import { ProductType } from 'types/Products';
import {
  ActiveFilter,
  Facets,
  FilterOption,
  SearchFilter,
  SearchTarget
} from 'types/Search';
import { Option, SegmentTag } from 'types/global';
import RecentlyLaunchedFilter from '../RecentlyLaunchedFilter/RecentlyLaunchedFilter';
import SearchFilterItem from '../SearchFilterItem/SearchFilterItem';
import './SearchFilters.scss';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CustomAttributesTypes } from 'components/common/Portals/CustomAttributes/types';
export interface SearchFiltersProps {
  facets: Facets;
  target: SearchTarget;
  activeFilters: SearchFilter[] | undefined;
  updateFilters: (filters: ActiveFilter[]) => void;
  isBrandPortal?: boolean;
  segment: SegmentTag;
}

const SearchFilters = (props: SearchFiltersProps) => {
  const { target, activeFilters, updateFilters, isBrandPortal, segment } =
    props;
  const { exhibitionsFilter } = useFlags();

  const years = useMemo(() => {
    const activeYears: string[] =
      activeFilters
        ?.filter(field => field.field === 'launch_year')
        ?.map(field => field.value) ?? [];

    return RECENTLY_LAUNCHED_FILTERS.map(el => ({
      ...el,
      active: activeYears.includes(el.year)
    }));
  }, [activeFilters]);

  const { classes, cx } = useStyles();

  const baseFilterOptions = useMemo(
    () => getFilterOptions(target, segment),
    [target, segment]
  );

  const filterOptions = useMemo(() => {
    return exhibitionsFilter?.enabled && target === 'SUPPLIERS'
      ? [
          ...baseFilterOptions,
          {
            key: 'exhibition_name',
            label: exhibitionsFilter?.title,
            value: exhibitionsFilter?.key,
            type: CustomAttributesTypes.TEXT
          }
        ]
      : baseFilterOptions;
  }, [baseFilterOptions, exhibitionsFilter, target]);

  const HEADER_TITLE =
    target === 'SUPPLIERS' ? (
      'Supplier filters'
    ) : target === ProductType.INGREDIENTS && isBrandPortal ? (
      'COVALO FILTERS'
    ) : (
      <>
        <Trans i18nKey={`reference.segments.${segment}`} /> filters
      </>
    );
  return (
    <Box
      className={cx('search-filters', classes.searchFilters)}
      sx={{
        // for brand portals with custom filters, we let this container grow and add a scrollbar
        // for others, we want it to fit the available space and not grow; scrolling is handled inside
        minHeight: props.isBrandPortal ? undefined : 0,
        flexGrow: 1
      }}
    >
      <div className="search-filters-body">
        <div className="search-filters-header">
          <div className="search-filters-header-title">{HEADER_TITLE}</div>
        </div>
        {segment === SegmentTag.PERSONALCARE && <Divider />}

        <ListGroup className="search-filters-body-list">
          {segment === SegmentTag.PERSONALCARE && props.target !== 'SUPPLIERS' && (
            <RecentlyLaunchedFilter
              updateFilter={(value: Option[]) => {
                const arr =
                  value.length > 0
                    ? value[0].value.map((v: string) => ({
                        value: v,
                        field: 'launch_year'
                      }))
                    : [];
                updateFilters([{ value: arr, property: 'launch_year' }]);
              }}
              years={years}
            />
          )}

          {filterOptions.map((option: FilterOption) => (
            <SearchFilterItem key={option.key} option={option} />
          ))}
        </ListGroup>
      </div>
    </Box>
  );
};

const useStyles = tss.create(({ theme }) => ({
  searchFilters: {
    '& .search-filter-item-checkbox input[type=checkbox]': {
      backgroundColor: `${theme.palette.primary.main}33`
    }
  }
}));

export default SearchFilters;
